/**
 * TopicDetailPanel (Sprint 16) — tabbed workspace for one ContentTopic.
 *
 * Tabs:
 *   Overview       — summary, themes, tags, meta, dataUsage (classic view)
 *   Research       — linked research packs + [Run Deep Research] trigger
 *   Templates      — per-platform template picker backed by
 *                    SystemTemplateBrowserDialog in pick mode
 *   Voice & Design — inherited vs override (read-only stub; full editor later)
 *   Posts          — [Generate posts] primary action (opens TopicPostGenerationDialog)
 *
 * Opened from the Backlog sub-tab (strategyId undefined) OR from the Level 2
 * Sprint Detail drill-down (strategyId + sprintId present). When no strategy
 * context, the "To sprint" destination in post generation is disabled.
 */

import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Chip, Stack, Alert, Tabs, Tab, Button, IconButton, Tooltip,
  TextField, CircularProgress, Divider,
  Collapse, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import { getProviderForModel, SDialog } from '@modules/ui-components';
import { AvailableModelSelector, useAvailableAIModels } from '@modules/ai-settings';
import type { ContentTopic, PublicationSlotAssignment } from '../types';
import { ContentPlanApiClient } from '../services/ContentPlanApiClient';
import { DataUsageList } from './DataUsageList';
import { useResolveSourcePath } from '../hooks/useResolveSourcePath';
import { useTopicVoiceDesign } from '../hooks/useTopicVoiceDesign';
import { useTopicPostGenerations } from '../hooks/useTopicPostGenerations';
import { TopicResourcesSection } from './topic/TopicResourcesSection';
import { TopicB2BReaderContractCard } from './topic/TopicB2BReaderContractCard';
import { PostsGrid } from './topic/PostsGrid';
import { PostBreakdownEditor } from './topic/PostBreakdownEditor';
import { TopicTemplatesTab } from './topic/TopicTemplatesTab';
import { usePillars } from '../hooks/usePillars';
import { useMasterPostsForTopic } from '../hooks/useMasterPostsForTopic';
import { ResearchRunCard } from './research/ResearchRunCard';
import { ResearchBriefEditor } from './research/ResearchBriefEditor';
import { ResearchAnalysisCard } from './research/ResearchAnalysisCard';
import { ResearchBriefInputForm } from './research/ResearchBriefInputForm';
import { ResearchStudiesTab } from './research/ResearchStudiesTab';
import { CellTaskBadge } from './topic/CellTaskBadge';
import { useTopicResearchRuns } from '../hooks/useTopicResearchRuns';
import { useThemeCatalog } from '../hooks/useThemeCatalog';
import { FailuresList } from './generations/FailuresList';
import { GenerationReviewDialog } from './generations/GenerationReviewDialog';
import type { GenerationRunSummary } from '../hooks/useGenerationRuns';
import { TopicPostGenerationDialog } from './TopicPostGenerationDialog';
import { UnifiedPostCell } from './postLike/UnifiedPostCell';
import { UnifiedPostDialog } from './postLike/UnifiedPostDialog';
import {
  BrandStyleKitEditor,
  brandStyleKitDraftToPatch,
  emptyBrandStyleKitDraft,
  profileToBrandStyleKitDraft,
  type BrandStyleKitDraft,
} from '@modules/content-plan/components/design/BrandStyleKitEditor';
import {
  contentPostToPostLike,
  postLikeToContentPostPatch,
  type PostLike,
} from '../utils/postLike';
import { useUpdateGenerationDraft } from '../hooks/useUpdateGenerationDraft';
import { StorageService } from '@modules/firebase-core/services/StorageService';
import type { ContentPost } from '../types';
import { useAuthor } from '@modules/content-plan/hooks/useAuthor';
import { useAuthorPlatforms } from '@modules/content-plan/hooks/useAuthorPlatforms';
import { useProjectDefaultModel } from '@modules/content-plan/hooks/useProjectDefaultModel';
import { useContentTopic } from '../hooks/useContentTopic';

export type TabKey = 'overview' | 'research' | 'templates' | 'voice' | 'posts';

type ResearchSearchProvider = 'auto' | 'perplexity' | 'gemini' | 'openai';
type ResearchDepth = 'standard' | 'deep';
type ResearchReasoningEffort = 'low' | 'medium' | 'high';

const SEARCH_PROVIDER_OPTIONS: Array<{ value: ResearchSearchProvider; label: string }> = [
  { value: 'auto', label: 'Auto' },
  { value: 'perplexity', label: 'Perplexity' },
  { value: 'gemini', label: 'Gemini' },
  { value: 'openai', label: 'OpenAI' },
];

const RESEARCH_DEPTH_OPTIONS: Array<{ value: ResearchDepth; label: string }> = [
  { value: 'deep', label: 'Deep' },
  { value: 'standard', label: 'Standard' },
];

const RESEARCH_EFFORT_OPTIONS: Array<{ value: ResearchReasoningEffort; label: string }> = [
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'low', label: 'Low' },
];

function defaultResearchModel(provider: Exclude<ResearchSearchProvider, 'auto'>, depth: ResearchDepth): string {
  if (provider === 'perplexity') {
    return depth === 'deep' ? 'sonar-deep-research' : 'sonar-pro';
  }
  if (provider === 'openai') {
    return depth === 'deep' ? 'gpt-5.6-sol' : 'gpt-5.6-terra';
  }
  return depth === 'deep' ? 'gemini-3.1-pro-preview' : 'gemini-3-flash-preview';
}

function searchProviderToAIProvider(provider: Exclude<ResearchSearchProvider, 'auto'>): 'google' | 'openai' | 'perplexity' {
  return provider === 'gemini' ? 'google' : provider;
}

function shouldResetResearchModel(
  model: string,
  searchProvider: Exclude<ResearchSearchProvider, 'auto'>,
  researchDepth: ResearchDepth,
): boolean {
  if (!model) return true;
  const expectedProvider = searchProviderToAIProvider(searchProvider);
  if (getProviderForModel(model) !== expectedProvider) return true;
  if (researchDepth === 'standard') return model.includes('deep-research');
  if (searchProvider === 'gemini') return model !== 'gemini-3.1-pro-preview';
  if (searchProvider === 'openai') return !model.includes('deep-research');
  if (searchProvider === 'perplexity') {
    return model !== 'sonar-pro' && model !== 'sonar-deep-research';
  }
  return false;
}

export interface TopicDetailPanelProps {
  open: boolean;
  onClose: () => void;
  topic: ContentTopic | null;
  projectId: string;
  userId: string;
  authorId?: string | null;
  /** Strategy id — required for scope='sprint' apply destination */
  strategyId?: string;
  sprintId?: string;
  /** Calendar publication slot context. Present only when opened from a slot chip. */
  publicationId?: string;
  publicationPlatform?: string;
  publicationAspectKey?: string | null;
  publicationSlotAssignment?: PublicationSlotAssignment | null;
  onPostsGenerated?: (taskId: string) => void;
  /**
   * When true, render the inner SDialog in MUI fullScreen mode (no
   * width/maxWidth limits, fills the viewport). Used by the standalone
   * Topic route (`/project/:pid/content-plan/topic/:tid`).
   *
   * @default false (existing in-app modal behaviour)
   */
  fullScreen?: boolean;
  /**
   * Controlled active tab. When BOTH `tab` AND `onTabChange` are provided,
   * the panel switches to controlled mode and the URL/route owner drives
   * tab state. When omitted, the panel reverts to its pre-existing
   * uncontrolled behaviour (resets to 'overview' on `open`/`topic.id` change).
   */
  tab?: TabKey;
  /** Controlled tab change callback (paired with `tab`). */
  onTabChange?: (next: TabKey) => void;
}

// ─── Subcomponent: Research tab (Sprint 18 — three-phase pipeline) ───
//   Step 1: Compose brief → edit queries/sources in place
//   Step 2: Run deep research (grounded web research) against the brief
//   Step 3: Analyse research → keyFacts, contentSeeds, relevance, coverage
//   Also: "Run all" for one-click end-to-end, and a per-topic history list.

const ResearchTab: React.FC<{
  projectId: string; userId: string; topic: ContentTopic;
  authorId: string;
  onTaskStarted?: (taskId: string) => void;
}> = ({ projectId, userId, topic, authorId }) => {
  const { runs, loading } = useTopicResearchRuns(projectId, topic.id, authorId);
  const { themes: themeCatalog } = useThemeCatalog(projectId);
  const projectDefaultModel = useProjectDefaultModel(projectId);
  const {
    providers: availableProviderMap,
    projectProviders,
    loading: availableModelsLoading,
  } = useAvailableAIModels(projectId);
  const themeNamesById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const t of themeCatalog ?? []) map[t.id] = t.name ?? t.id;
    return map;
  }, [themeCatalog]);
  // Minimal project preview read (one-shot; picker components below fetch
  // their own copies). Kept optional — undefined is fine.
  const [projectPreview, setProjectPreview] = useState<
    | { name?: string; domain?: string; audienceSummary?: string; targetCountries?: string[] }
    | undefined
  >(undefined);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { CoreFirebaseService } = await import(
          '@modules/firebase-core/services/CoreFirebaseService'
        );
        const db = CoreFirebaseService.getInstance().firestore;
        const snap = await getDoc(doc(db, 'projects', projectId));
        if (cancelled || !snap.exists()) return;
        const data = snap.data() as any;
        setProjectPreview({
          name: data?.name,
          domain: data?.domain ?? data?.industry,
          audienceSummary: data?.audienceSummary ?? data?.audience,
          targetCountries: Array.isArray(data?.targetCountries) ? data.targetCountries : [],
        });
      } catch {
        /* non-critical */
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);        // 'brief' | 'execute' | 'analyse' | 'run_all'
  const selectedRun = useMemo(
    () => runs.find((r: any) => r.id === selectedRunId) ?? null,
    [runs, selectedRunId],
  );

  // Per-phase model selection. Brief + analysis use the full model selector;
  // research resolves through a grounded web provider selector.
  const [modelBrief, setModelBrief] = useState<string>(projectDefaultModel.value);
  const [searchProvider, setSearchProvider] = useState<ResearchSearchProvider>('auto');
  const [researchDepth, setResearchDepth] = useState<ResearchDepth>('deep');
  const [reasoningEffort, setReasoningEffort] = useState<ResearchReasoningEffort>('medium');
  const [modelResearch, setModelResearch] = useState<string>(defaultResearchModel('perplexity', 'deep'));
  const [modelAnalysis, setModelAnalysis] = useState<string>(projectDefaultModel.value);
  const [modelsExpanded, setModelsExpanded] = useState<boolean>(false);
  const [fullTextOpen, setFullTextOpen] = useState<boolean>(false);

  React.useEffect(() => {
    setModelBrief(projectDefaultModel.value);
    setModelAnalysis(projectDefaultModel.value);
  }, [projectDefaultModel.value, topic.id]);

  React.useEffect(() => {
    if (searchProvider === 'auto') return;
    const nextDefault = defaultResearchModel(searchProvider, researchDepth);
    if (shouldResetResearchModel(modelResearch, searchProvider, researchDepth)) {
      setModelResearch(nextDefault);
    }
  }, [modelResearch, researchDepth, searchProvider]);

  const availableSearchProviderOptions = useMemo(() => {
    if (availableModelsLoading) return SEARCH_PROVIDER_OPTIONS;
    const hasProjectConfiguredProvider = SEARCH_PROVIDER_OPTIONS.some((option) => (
      option.value !== 'auto' && projectProviders[searchProviderToAIProvider(option.value)]?.configured
    ));
    return SEARCH_PROVIDER_OPTIONS.filter((option) => {
      if (option.value === 'auto') return true;
      const provider = searchProviderToAIProvider(option.value);
      if (hasProjectConfiguredProvider) return Boolean(projectProviders[provider]?.configured);
      return Boolean(availableProviderMap[provider]?.configured && availableProviderMap[provider]?.textModels);
    });
  }, [availableModelsLoading, availableProviderMap, projectProviders]);

  React.useEffect(() => {
    if (availableModelsLoading || searchProvider === 'auto') return;
    if (availableSearchProviderOptions.some((option) => option.value === searchProvider)) return;
    setSearchProvider('auto');
  }, [availableModelsLoading, availableSearchProviderOptions, searchProvider]);

  const explicitResearchModel =
    searchProvider === 'auto'
      ? null
      : modelResearch || defaultResearchModel(searchProvider, researchDepth);

  // Single-doc model: run envelope carries brief / research / analysis inline.
  // No separate subdoc loaders needed — just read from selectedRun.
  const brief = (selectedRun as any)?.brief ?? null;
  const rawResearch = (selectedRun as any)?.research ?? null;
  const analysis = (selectedRun as any)?.analysis ?? null;

  // Smart auto-select: prefer the latest run whose brief phase completed.
  // Failed runs are skipped so the UI doesn't freeze on a stale failure.
  React.useEffect(() => {
    if (selectedRunId && runs.some((r: any) => r.id === selectedRunId)) return;
    if (runs.length === 0) {
      if (selectedRunId !== null) setSelectedRunId(null);
      return;
    }
    const firstHealthy = runs.find(
      (r: any) => r?.phases?.brief?.status === 'completed' && r?.brief,
    );
    setSelectedRunId((firstHealthy ?? runs[0]).id);
  }, [runs, selectedRunId]);

  const client = useMemo(() => new ContentPlanApiClient(), []);

  /**
   * Compose or recompose brief. `reuseRunId` (typically the selected run)
   * keeps the envelope the same — orchestrator overwrites `run.brief`
   * inline. Omit it to create a fresh research run.
   */
  const handleComposeBrief = async (
    payload: {
      userHints?: string;
      selectedPaths: string[];
      hydratedData: unknown;
    },
    reuseRunId?: string | null,
  ) => {
    setError(null); setBusy('brief');
    try {
      const resp = await client.composeTopicResearchBrief({
        projectId, userId, authorId, topicId: topic.id,
        userHints: payload.userHints,
        manualSelectionPaths: payload.selectedPaths,
        hydratedData: payload.hydratedData,
        runId: reuseRunId ?? undefined,
        models: {
          brief: modelBrief,
          research: explicitResearchModel,
          analysis: modelAnalysis,
        },
        searchProvider,
        researchDepth,
        reasoningEffort,
      } as any);
      if (!resp.success) throw new Error(resp.error ?? 'brief start failed');
      if (resp.runId) setSelectedRunId(resp.runId);
    } catch (e) {
      setError((e as Error).message);
    }
    finally { setBusy(null); }
  };

  const handleRunDeepResearch = async () => {
    if (!selectedRunId) return;
    setError(null); setBusy('execute');
    try {
      const resp = await client.executeResearch({
        projectId,
        userId,
        authorId,
        runId: selectedRunId,
        model: explicitResearchModel,
        searchProvider,
        researchDepth,
        reasoningEffort,
      });
      if (!resp.success) throw new Error(resp.error ?? 'deep-research start failed');
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  };

  const handleAnalyse = async () => {
    if (!selectedRunId) return;
    setError(null); setBusy('analyse');
    try {
      const resp = await client.analyseResearch({
        projectId, userId, authorId, runId: selectedRunId, model: modelAnalysis,
      });
      if (!resp.success) throw new Error(resp.error ?? 'analysis start failed');
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  };

  const handleRunAll = async () => {
    setError(null); setBusy('run_all');
    try {
      const resp = await client.runAllTopicResearch({
        projectId, userId, authorId, topicId: topic.id,
        models: {
          brief: modelBrief,
          research: explicitResearchModel,
          analysis: modelAnalysis,
        },
        searchProvider,
        researchDepth,
        reasoningEffort,
      });
      if (!resp.success) throw new Error(resp.error ?? 'run-all start failed');
      setSelectedRunId(resp.runId ?? null);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  };

  const handleSaveBrief = async (patch: Record<string, unknown>) => {
    if (!selectedRunId) return;
    setError(null);
    try {
      const resp = await client.patchResearchBrief({
        projectId, authorId, runId: selectedRunId, patch,
      });
      if (!resp.success) throw new Error(resp.error ?? 'save brief failed');
      // No local setBrief — envelope subscription picks up the updated
      // `run.brief` field and re-renders automatically.
    } catch (e) { setError((e as Error).message); }
  };

  const handleDeleteRun = async (runId: string) => {
    setError(null);
    try {
      await client.deleteResearchRun({ projectId, authorId, runId });
      if (runId === selectedRunId) setSelectedRunId(null);
    } catch (e) { setError((e as Error).message); }
  };

  const briefReady = Boolean(brief);
  const rawReady = Boolean(rawResearch);
  const analysisReady = Boolean(analysis);

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant="contained"
          size="small"
          startIcon={busy === 'run_all' ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
          onClick={handleRunAll}
          disabled={!!busy}
          data-testid="topic-research-run-all"
        >
          {busy === 'run_all' ? 'Starting…' : 'Run all (3 phases)'}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          or run step-by-step:
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="text"
          startIcon={<TuneIcon />}
          endIcon={
            <ExpandMoreIcon
              sx={{
                transform: modelsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          }
          onClick={() => setModelsExpanded((v) => !v)}
          data-testid="research-models-toggle"
        >
          Models {modelsExpanded ? '' : '(advanced)'}
        </Button>
      </Stack>

      <Collapse in={modelsExpanded}>
        <Stack
          spacing={1.25}
          sx={{
            p: 1.5,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 0.5,
            bgcolor: 'background.default',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: { md: 170 } }}>
              <Chip size="small" label="Step 1" />
              <Typography variant="caption" color="text.secondary">Compose brief</Typography>
            </Stack>
            <AvailableModelSelector
              projectId={projectId}
              value={modelBrief}
              onChange={setModelBrief}
              label="Brief model"
              sx={{ flex: 1, minWidth: 0 }}
              testId="research-model-brief"
            />
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
            useFlexGap
            sx={{ flexWrap: 'wrap' }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: { md: 170 } }}>
              <Chip size="small" label="Step 2" />
              <Typography variant="caption" color="text.secondary">Deep research</Typography>
            </Stack>
            <FormControl size="small" sx={{ flex: '0 1 150px', minWidth: 140 }}>
              <InputLabel>Provider</InputLabel>
              <Select
                value={searchProvider}
                label="Provider"
                onChange={(e) => setSearchProvider(e.target.value as ResearchSearchProvider)}
                data-testid="research-search-provider"
              >
                {availableSearchProviderOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: '0 1 140px', minWidth: 130 }}>
              <InputLabel>Depth</InputLabel>
              <Select
                value={researchDepth}
                label="Depth"
                onChange={(e) => setResearchDepth(e.target.value as ResearchDepth)}
                data-testid="research-depth"
              >
                {RESEARCH_DEPTH_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {searchProvider !== 'auto' && (
              <AvailableModelSelector
                projectId={projectId}
                value={modelResearch}
                onChange={setModelResearch}
                label="Research model"
                providers={[searchProviderToAIProvider(searchProvider)]}
                modelUsage={researchDepth === 'deep' ? 'deep-research' : 'general'}
                size="small"
                sx={{ flex: '1 1 220px', minWidth: 210 }}
                testId="research-model-research"
              />
            )}
            <FormControl size="small" sx={{ flex: '0 1 150px', minWidth: 140 }}>
              <InputLabel>Effort</InputLabel>
              <Select
                value={reasoningEffort}
                label="Effort"
                onChange={(e) => setReasoningEffort(e.target.value as ResearchReasoningEffort)}
                data-testid="research-reasoning-effort"
              >
                {RESEARCH_EFFORT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: { md: 170 } }}>
              <Chip size="small" label="Step 3" />
              <Typography variant="caption" color="text.secondary">Analyse research</Typography>
            </Stack>
            <AvailableModelSelector
              projectId={projectId}
              value={modelAnalysis}
              onChange={setModelAnalysis}
              label="Analysis model"
              sx={{ flex: 1, minWidth: 0 }}
              testId="research-model-analysis"
            />
          </Stack>
        </Stack>
      </Collapse>

      {/* Step 1 — Brief input form + editor */}
      <Box
        sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 0.5 }}
        data-testid="research-step-brief"
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Chip size="small" label="Step 1" />
          <Typography variant="subtitle2" sx={{ flex: 1 }}>Compose brief</Typography>
          {(selectedRun?.phases?.brief?.status === 'running'
            || selectedRun?.phases?.brief?.status === 'pending')
            && selectedRun?.phases?.brief?.taskId && (
            <CellTaskBadge
              taskId={selectedRun.phases.brief.taskId as string}
              data-testid="research-step-brief-task-badge"
            />
          )}
          {selectedRun?.phases?.brief?.status && (
            <Chip
              size="small"
              color={STEP_STATUS_COLOR[selectedRun.phases.brief.status] ?? 'default'}
              variant="outlined"
              label={selectedRun.phases.brief.status}
            />
          )}
        </Stack>

        {/* Input form — shown before brief exists AND when user wants to recompose */}
        {!briefReady && (
          <ResearchBriefInputForm
            projectId={projectId}
            topic={topic}
            themeNamesById={themeNamesById}
            projectPreview={projectPreview}
            busy={busy === 'brief'}
            briefExists={false}
            onCompose={handleComposeBrief}
          />
        )}

        {/* Inline editor after brief appears */}
        {briefReady && (
          <Stack spacing={1}>
            <ResearchBriefEditor
              brief={brief}
              saving={false}
              onSave={handleSaveBrief}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<AutoFixHighIcon />}
              onClick={() =>
                // Pass selectedRunId so the orchestrator overwrites the
                // existing `run.brief` instead of creating a new envelope.
                handleComposeBrief(
                  { selectedPaths: [], hydratedData: undefined as any },
                  selectedRunId,
                )
              }
              disabled={!!busy}
              data-testid="research-brief-recompose"
            >
              {busy === 'brief' ? 'Recomposing…' : 'Recompose brief (fresh AI pass)'}
            </Button>
          </Stack>
        )}
      </Box>

      {/* Step 2 — Deep research */}
      <StepPanel
        index={2}
        title="Deep research"
        status={selectedRun?.phases?.research?.status}
        taskId={selectedRun?.phases?.research?.taskId as string | undefined}
        testId="research-step-deep-research"
        actionLabel="Run deep research"
        onAction={handleRunDeepResearch}
        busy={busy === 'execute'}
        enabled={briefReady}
        disabledHint="Compose the brief first."
      >
        {(selectedRun?.phases?.research?.provider || selectedRun?.phases?.research?.model) && (
          <Typography variant="caption" color="text.secondary">
            {[
              selectedRun?.phases?.research?.provider,
              selectedRun?.phases?.research?.model,
              selectedRun?.phases?.research?.researchDepth,
              selectedRun?.phases?.research?.reasoningEffort,
            ].filter(Boolean).join(' · ')}
          </Typography>
        )}
        {rawReady && (
          <Box sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {(rawResearch?.chunks ?? []).length} citations · {String(rawResearch?.aiSummary ?? '').length.toLocaleString()} chars
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInFullIcon fontSize="small" />}
                onClick={() => setFullTextOpen(true)}
                data-testid="research-view-full-text"
              >
                View full text
              </Button>
            </Stack>
            <Box
              sx={{
                maxHeight: 280, overflow: 'auto', p: 1,
                border: '1px solid', borderColor: 'divider', borderRadius: 0.5,
              }}
            >
              <Typography
                variant="caption"
                component="pre"
                sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.8rem' }}
              >
                {String(rawResearch?.aiSummary ?? '').slice(0, 2000)}
                {String(rawResearch?.aiSummary ?? '').length > 2000 && '\n\n…'}
              </Typography>
            </Box>
          </Box>
        )}
      </StepPanel>

      {/* Full-text deep research popup — untruncated aiSummary + citations + grounded HTML. */}
      <ResearchFullTextDialog
        open={fullTextOpen}
        onClose={() => setFullTextOpen(false)}
        rawResearch={rawResearch}
      />

      {/* Step 3 — Analyse */}
      <StepPanel
        index={3}
        title="Analyse research"
        status={selectedRun?.phases?.analysis?.status}
        taskId={selectedRun?.phases?.analysis?.taskId as string | undefined}
        testId="research-step-analysis"
        actionLabel="Analyse research"
        onAction={handleAnalyse}
        busy={busy === 'analyse'}
        enabled={rawReady}
        disabledHint="Run deep research first."
      >
        {analysisReady && (
          <ResearchAnalysisCard
            analysis={analysis}
            projectId={projectId}
            runId={selectedRunId ?? undefined}
            userId={userId}
          />
        )}
      </StepPanel>

      <Divider />

      {/* History */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          History ({runs.length})
        </Typography>
        {loading && runs.length === 0 && (
          <Typography variant="caption" color="text.secondary">Loading…</Typography>
        )}
        {!loading && runs.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No research runs yet. Use "Run all" or the step buttons above to create one.
          </Typography>
        )}
        <Stack spacing={1}>
          {runs.map((r: any) => (
            <ResearchRunCard
              key={r.id}
              run={r}
              onOpen={setSelectedRunId}
              onDelete={handleDeleteRun}
            />
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};

// ─── Subcomponent: Full-text deep research popup (Step 2) ────────────
//
// Replaces the inline 2000-char-sliced 280px scrollbox. Shows the entire
// `aiSummary` markdown without truncation, the citations list (chunks with
// title + url + domain) and the grounded-search entry-point HTML when present
// (rendered via sandboxed iframe per Sprint 17 ToS-compliant rendering).
const ResearchFullTextDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  rawResearch: any;
}> = ({ open, onClose, rawResearch }) => {
  const aiSummary = String((rawResearch as any)?.aiSummary ?? '');
  const chunks = ((rawResearch as any)?.chunks ?? []) as Array<{
    title?: string;
    url?: string;
    domain?: string;
    excerpt?: string;
    redirectUrl?: string;
  }>;
  const searchEntryPointHtml = (rawResearch as any)?.searchEntryPointHtml as string | undefined;

  return (
    <SDialog
      open={open}
      onClose={onClose}
      title="Deep research — full text"
      maxWidth="lg"
      fullWidth
      data-testid="research-full-text-dialog"
    >
      <Stack spacing={2} sx={{ py: 1, maxHeight: '70vh', overflow: 'auto' }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {chunks.length} citation{chunks.length === 1 ? '' : 's'} · {aiSummary.length.toLocaleString()} chars
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom>AI summary</Typography>
          <Box
            sx={{
              p: 1.5,
              border: '1px solid', borderColor: 'divider', borderRadius: 0.5,
              bgcolor: 'background.default',
            }}
          >
            <Typography
              variant="body2"
              component="pre"
              sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', m: 0 }}
              data-testid="research-full-text-summary"
            >
              {aiSummary || '(empty)'}
            </Typography>
          </Box>
        </Box>

        {chunks.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Citations ({chunks.length})
            </Typography>
            <Stack spacing={0.75}>
              {chunks.map((c, i) => (
                <Box
                  key={i}
                  sx={{
                    p: 1,
                    border: '1px solid', borderColor: 'divider', borderRadius: 0.5,
                  }}
                >
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 24 }}>
                      [{i}]
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                      {c.title ?? c.url ?? '(untitled)'}
                    </Typography>
                    {c.domain && (
                      <Chip size="small" label={c.domain} variant="outlined" />
                    )}
                  </Stack>
                  {c.url && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="a"
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'block', mt: 0.25, wordBreak: 'break-all' }}
                    >
                      {c.url}
                    </Typography>
                  )}
                  {c.excerpt && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                      {c.excerpt}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {searchEntryPointHtml && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Google Search Suggestions
            </Typography>
            <Box
              component="iframe"
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              srcDoc={searchEntryPointHtml}
              sx={{
                width: '100%',
                minHeight: 120,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 0.5,
              }}
            />
          </Box>
        )}
      </Stack>
    </SDialog>
  );
};

interface StepPanelProps {
  index: number;
  title: string;
  status?: string;
  /** Phase 3 — backend_tasks/{taskId} pointer for the active phase. When set
   *  AND status is `running|pending`, render a live CellTaskBadge with the
   *  current phase displayName, sourced from the global background-tasks store. */
  taskId?: string | null;
  actionLabel: string;
  onAction: () => void;
  busy: boolean;
  enabled: boolean;
  disabledHint?: string;
  children?: React.ReactNode;
  /** E2E testid root. */
  testId?: string;
}

const STEP_STATUS_COLOR: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
  pending: 'default',
  running: 'primary',
  completed: 'success',
  failed: 'error',
  skipped: 'default',
};

const StepPanel: React.FC<StepPanelProps> = ({
  index, title, status, taskId, actionLabel, onAction, busy, enabled, disabledHint, children, testId,
}) => {
  const showLive = (status === 'running' || status === 'pending') && Boolean(taskId);
  return (
    <Box
      sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 0.5 }}
      data-testid={testId}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Chip size="small" label={`Step ${index}`} />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>{title}</Typography>
        {showLive && (
          <CellTaskBadge
            taskId={taskId}
            data-testid={testId ? `${testId}-task-badge` : undefined}
          />
        )}
        {status && (
          <Chip
            size="small"
            color={STEP_STATUS_COLOR[status] ?? 'default'}
            variant="outlined"
            label={status}
          />
        )}
        <Button
          size="small"
          variant="contained"
          disabled={busy || !enabled}
          onClick={onAction}
          startIcon={busy ? <CircularProgress size={12} /> : null}
          data-testid={`research-step-${index}-action`}
        >
          {busy ? 'Starting…' : actionLabel}
        </Button>
      </Stack>
      {!enabled && disabledHint && (
        <Typography variant="caption" color="text.secondary">{disabledHint}</Typography>
      )}
      {enabled && children}
    </Box>
  );
};

// ─── Subcomponent: Voice & Design tab (minimal v1) ───────────────────

// ─── Source-chip helpers ─────────────────────────────────────────────
//
// The Voice & Design editor shows provenance (where the current value came
// from) next to a freeform editor that can override it at topic scope.
// `source` comes from useTopicVoiceDesign — resolver precedence mirrors
// backend voiceDesignResolver (topic override → strategy → project).

type VoiceSource = 'topic_override' | 'strategy' | 'project' | 'none';

const SOURCE_LABELS: Record<VoiceSource, { label: string; color: 'primary' | 'info' | 'default' | 'warning' }> = {
  topic_override: { label: 'Override (this topic)', color: 'primary' },
  strategy: { label: 'Inherited from strategy', color: 'info' },
  project: { label: 'Inherited from project', color: 'info' },
  none: { label: 'No value set', color: 'warning' },
};

/** Serialize any value for the textarea — objects → pretty JSON, strings → as-is. */
function toEditable(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try { return JSON.stringify(value, null, 2); } catch { return String(value); }
}

/** Parse back — try JSON first; if the value is invalid JSON AND looks like
 *  plain text (no leading `{` or `[`), keep it as a string. This matches the
 *  project-level freeform `toneOfVoice` field that's a plain description. */
function fromEditable(raw: string): unknown {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const looksLikeJson = trimmed.startsWith('{') || trimmed.startsWith('[');
  if (looksLikeJson) {
    try { return JSON.parse(trimmed); } catch { /* fall through */ }
  }
  return trimmed;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function designValueToDraft(value: unknown): BrandStyleKitDraft {
  if (typeof value === 'string') {
    return {
      ...emptyBrandStyleKitDraft(),
      designDescription: value,
    };
  }
  if (!isRecord(value)) return emptyBrandStyleKitDraft();
  const looksLikeProfile = 'designSystem' in value
    || 'designDescription' in value
    || 'visualReferences' in value
    || 'forbiddenVisuals' in value;
  if (looksLikeProfile) return profileToBrandStyleKitDraft(value as any);
  return profileToBrandStyleKitDraft({ designSystem: value } as any);
}

const VoiceDesignTab: React.FC<{
  topic: ContentTopic;
  projectId: string;
  strategyId?: string;
}> = ({ topic, projectId, strategyId }) => {
  const { tone, design, inheritedTone, inheritedDesign, loading,
    saveToneOverride, saveDesignOverride, clearToneOverride, clearDesignOverride,
  } = useTopicVoiceDesign(projectId, topic.id, strategyId ?? null);

  // Local draft state for the editor — commits to Firestore on Save.
  const [toneDraft, setToneDraft] = useState<string>('');
  const [designDraft, setDesignDraft] = useState<BrandStyleKitDraft>(() => emptyBrandStyleKitDraft());
  const [toneDirty, setToneDirty] = useState(false);
  const [designDirty, setDesignDirty] = useState(false);
  const [toneError, setToneError] = useState<string | null>(null);
  const [designError, setDesignError] = useState<string | null>(null);
  const [savingTone, setSavingTone] = useState(false);
  const [savingDesign, setSavingDesign] = useState(false);

  // Seed the draft from the resolved value once per topic (or when the
  // incoming value changes while the field is not dirty). This auto-copies
  // the strategy/project value the first time user opens the tab — exactly
  // the UX the user asked for: "if no strategy, copy from project and show".
  React.useEffect(() => {
    if (!toneDirty) setToneDraft(toEditable(tone.value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tone.value, topic.id]);
  React.useEffect(() => {
    if (!designDirty) setDesignDraft(designValueToDraft(design.value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [design.value, topic.id]);

  const toneIsOverride = tone.source === 'topic_override';
  const designIsOverride = design.source === 'topic_override';

  const handleSaveTone = async () => {
    setSavingTone(true); setToneError(null);
    try {
      const parsed = fromEditable(toneDraft);
      await saveToneOverride(parsed);
      setToneDirty(false);
    } catch (e) {
      setToneError((e as Error).message);
    } finally {
      setSavingTone(false);
    }
  };

  const handleSaveDesign = async () => {
    setSavingDesign(true); setDesignError(null);
    try {
      const patch = brandStyleKitDraftToPatch('topic', designDraft, 'manual');
      const nextDesignSystem = patch.designSystem;
      if (!nextDesignSystem || Object.keys(nextDesignSystem).length === 0) {
        throw new Error('Add at least one structured design value before saving a topic design override.');
      }
      await saveDesignOverride(nextDesignSystem);
      setDesignDirty(false);
    } catch (e) {
      setDesignError((e as Error).message);
    } finally {
      setSavingDesign(false);
    }
  };

  const handleResetToneFromInherited = () => {
    setToneDraft(toEditable(inheritedTone.value));
    setToneDirty(true);
  };

  const handleResetDesignFromInherited = () => {
    setDesignDraft(designValueToDraft(inheritedDesign.value));
    setDesignDirty(true);
  };

  const handleClearTone = async () => {
    setSavingTone(true); setToneError(null);
    try {
      await clearToneOverride();
      setToneDirty(false);
    } catch (e) {
      setToneError((e as Error).message);
    } finally {
      setSavingTone(false);
    }
  };

  const handleClearDesign = async () => {
    setSavingDesign(true); setDesignError(null);
    try {
      await clearDesignOverride();
      setDesignDirty(false);
    } catch (e) {
      setDesignError((e as Error).message);
    } finally {
      setSavingDesign(false);
    }
  };

  const toneSourceMeta = SOURCE_LABELS[tone.source as VoiceSource];
  const designSourceMeta = SOURCE_LABELS[design.source as VoiceSource];

  return (
    <Stack spacing={3}>
      {/* ── Tone of voice ─────────────────────────────────────── */}
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Tone of voice</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip
              size="small"
              label={toneSourceMeta.label}
              color={toneSourceMeta.color}
              variant={toneIsOverride ? 'filled' : 'outlined'}
            />
            {!toneIsOverride && inheritedTone.source !== 'none' && (
              <Tooltip title="Copy inherited value into the editor so you can tweak it for this topic">
                <Button size="small" variant="text" onClick={handleResetToneFromInherited} disabled={loading}>
                  Customize for this topic
                </Button>
              </Tooltip>
            )}
          </Stack>
        </Stack>
        <TextField
          value={toneDraft}
          onChange={(e) => { setToneDraft(e.target.value); setToneDirty(true); }}
          multiline minRows={5} maxRows={16} fullWidth size="small"
          placeholder={loading ? 'Loading…' : 'Plain text or JSON (ToneOfVoiceProfile shape)'}
          slotProps={{ input: { sx: { fontFamily: 'monospace', fontSize: '0.85rem' } } }}
          data-testid="topic-tone-editor"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Accepts plain string (legacy project tone) or JSON object (structured ToneOfVoiceProfile).
          Resolver precedence at generation time: <code>topic override → strategy → project</code>.
        </Typography>
        {toneError && <Alert severity="error" sx={{ mt: 1 }}>{toneError}</Alert>}
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            variant="contained" size="small"
            onClick={handleSaveTone}
            disabled={savingTone || !toneDirty}
            data-testid="topic-tone-save"
          >
            {savingTone ? 'Saving…' : (toneIsOverride ? 'Save override' : 'Save as override')}
          </Button>
          {toneIsOverride && (
            <Button
              variant="outlined" size="small" color="warning"
              onClick={handleClearTone}
              disabled={savingTone}
              data-testid="topic-tone-clear"
            >
              Clear override
            </Button>
          )}
        </Stack>
      </Box>

      {/* ── Design system ─────────────────────────────────────── */}
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Design system</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip
              size="small"
              label={designSourceMeta.label}
              color={designSourceMeta.color}
              variant={designIsOverride ? 'filled' : 'outlined'}
            />
            {!designIsOverride && inheritedDesign.source !== 'none' && (
              <Tooltip title="Copy inherited value into the editor so you can tweak it for this topic">
                <Button size="small" variant="text" onClick={handleResetDesignFromInherited} disabled={loading}>
                  Customize for this topic
                </Button>
              </Tooltip>
            )}
          </Stack>
        </Stack>
        <Alert severity="info" sx={{ mb: 1 }}>
          Topic overrides store structured design tokens only. Visual references still come from the active platform design profile in v1.
        </Alert>
        <BrandStyleKitEditor
          draft={designDraft}
          onChange={(nextDraft) => {
            setDesignDraft(nextDraft);
            setDesignDirty(true);
          }}
          profileKey="design"
          platformId="topic"
          disabled={loading || savingDesign}
          onReferenceMediaChange={() => undefined}
          showReviewed={false}
          showReferences={false}
          showDesignBrief={false}
          testIdPrefix="topic-design"
        />
        {designError && <Alert severity="error" sx={{ mt: 1 }}>{designError}</Alert>}
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            variant="contained" size="small"
            onClick={handleSaveDesign}
            disabled={savingDesign || !designDirty}
            data-testid="topic-design-save"
          >
            {savingDesign ? 'Saving…' : (designIsOverride ? 'Save override' : 'Save as override')}
          </Button>
          {designIsOverride && (
            <Button
              variant="outlined" size="small" color="warning"
              onClick={handleClearDesign}
              disabled={savingDesign}
              data-testid="topic-design-clear"
            >
              Clear override
            </Button>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

// ─── Subcomponent: Posts tab (generation history + trigger) ─────────

const PostsTabInline: React.FC<{
  topic: ContentTopic;
  projectId: string;
  userId: string;
  authorId: string;
  strategyId?: string;
  onOpenGenerateDialog: () => void;
}> = ({ topic, projectId, userId, authorId, strategyId, onOpenGenerateDialog }) => {
  const { runs, loading, deleteRun } = useTopicPostGenerations(projectId, topic.id, authorId);
  const [confirmingDelete, setConfirmingDelete] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState<string | null>(null);
  const [reviewRun, setReviewRun] = React.useState<GenerationRunSummary | null>(null);
  const [editingDraft, setEditingDraft] = React.useState<{
    runId: string;
    draft: ContentPost;
  } | null>(null);
  const { updateDraft } = useUpdateGenerationDraft(projectId, editingDraft?.runId ?? null);

  const handleApplyPosts = async (acceptedItemIds: string[]) => {
    if (!reviewRun) return;
    const runSprintId = (reviewRun as any).sprintId as string | undefined;
    const client = new ContentPlanApiClient();
    if (runSprintId) {
      // Standard path — promote drafts into the sprint's canonical posts
      // subcollection with scheduled time slots.
      const res = await client.applyPosts({
        projectId,
        authorId,
        generationId: reviewRun.id,
        acceptedItemIds,
        scope: 'sprint',
        sprintId: runSprintId,
      });
      if (!res.success) throw new Error(res.error ?? 'apply failed');
      return;
    }
    // Sprint 17 fallback — no sprint context, so save accepted drafts in
    // place and link them onto the originating topic via `usedInPostIds`.
    const res = await client.applyPosts({
      projectId,
      authorId,
      generationId: reviewRun.id,
      acceptedItemIds,
      scope: 'topic',
      topicId: topic.id,
    });
    if (!res.success) throw new Error(res.error ?? 'save to topic failed');
  };

  return (
    <Stack spacing={2} sx={{ py: 1 }}>
      <Typography variant="body2" color="text.secondary">
        Generate a fresh batch of posts from this topic. Supports multiple platforms,
        multiple variants per platform, and optional narrative series mode.
      </Typography>
      <Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<AutoFixHighIcon />}
          onClick={onOpenGenerateDialog}
          data-testid="topic-generate-posts-open"
        >
          Generate posts
        </Button>
      </Box>
      {!strategyId && (
        <Alert severity="info">
          Opened without a strategy context — the "To sprint" destination is disabled.
          Generated posts can still land as topic drafts or as Post Examples.
        </Alert>
      )}

      {/* Generation history for this topic — Sprint 16 Part C */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Generation history ({runs.length})
        </Typography>
        {loading && runs.length === 0 && (
          <Typography variant="caption" color="text.secondary">Loading history…</Typography>
        )}
        {!loading && runs.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No generation runs yet. Hit Generate to create the first batch.
          </Typography>
        )}
        <Stack spacing={1}>
          {runs.map((run: any) => {
            const generated = run.generatedItemCount ?? 0;
            const applied = run.appliedCount ?? 0;
            const failureCount = run.failureCount ?? 0;
            const failures = Array.isArray(run.failures) ? run.failures : [];
            const createdAt = run.createdAt
              ? (typeof run.createdAt === 'string'
                  ? new Date(run.createdAt).toLocaleString()
                  : new Date((run.createdAt as any)?.seconds * 1000).toLocaleString())
              : '—';
            return (
              <Box
                key={run.id}
                onClick={() => { if (generated > 0) setReviewRun(run); }}
                sx={{
                  p: 1.25,
                  borderLeft: '2px solid',
                  borderColor: failureCount > 0 ? 'warning.main' : 'primary.main',
                  borderRadius: 0.5,
                  bgcolor: 'action.hover',
                  cursor: generated > 0 ? 'pointer' : 'default',
                  transition: 'background-color 0.15s',
                  '&:hover': generated > 0 ? { bgcolor: 'action.selected' } : undefined,
                }}
                data-testid={`topic-post-run-${run.id}`}
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={run.status ?? 'unknown'}
                    color={run.status === 'applied' ? 'success' : (failureCount > 0 ? 'warning' : 'default')} />
                  <Chip size="small" variant="outlined" label={`${generated} generated`} />
                  {applied > 0 && <Chip size="small" variant="outlined" color="success" label={`${applied} applied`} />}
                  {failureCount > 0 && (
                    <Chip size="small" color="warning" label={`${failureCount} failed`} />
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    {createdAt}
                  </Typography>
                  <Tooltip title={confirmingDelete === run.id ? 'Click again to confirm' : 'Delete generation run + all its drafts'}>
                    <span>
                      <IconButton
                        size="small"
                        color={confirmingDelete === run.id ? 'error' : 'default'}
                        disabled={deleting === run.id}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (confirmingDelete !== run.id) {
                            setConfirmingDelete(run.id);
                            // Reset confirm after 4s so stray clicks don't trip.
                            setTimeout(() => setConfirmingDelete((v) => v === run.id ? null : v), 4000);
                            return;
                          }
                          setDeleting(run.id);
                          try { await deleteRun(run.id); } catch { /* non-critical */ }
                          setDeleting(null);
                          setConfirmingDelete(null);
                        }}
                        data-testid={`topic-post-run-delete-${run.id}`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                {run.model && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    model: <code>{run.model}</code>
                    {run.asSeries && ` · series (${run.seriesFlavor ?? 'continuation'})`}
                    {run.standalone && ' · standalone'}
                  </Typography>
                )}
                {failures.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <FailuresList failures={failures} compact />
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* Review dialog — opens on row click. Renders each draft via
          renderItem + renderItemDetails. Apply pathway routes sprint-scoped
          drafts through ContentPlanApiClient.applyPosts with scope='sprint';
          standalone runs (no sprintId on the run doc) fall through to
          scope='topic' which pins drafts to `topic.usedInPostIds` in place. */}
      <GenerationReviewDialog
        open={Boolean(reviewRun)}
        onClose={() => setReviewRun(null)}
        projectId={projectId}
        entity="post"
        run={reviewRun}
        titleSuffix={reviewRun ? `${(reviewRun.generatedItemCount ?? 0)} drafts` : undefined}
        applyLabel={
          reviewRun && !(reviewRun as any).sprintId ? 'Save to topic' : undefined
        }
        onApply={handleApplyPosts}
        renderItem={(draft) => {
          // Close-animation guard — MUI dialogs re-render items briefly while
          // closing; reviewRun may already be null. Returning null keeps the
          // animation smooth without feeding empty ids into the adapter.
          if (!reviewRun) return null;
          const data = draft.data as unknown as ContentPost;
          const postLike = contentPostToPostLike(data, {
            kind: 'content_post_draft',
            projectId,
            runId: reviewRun.id,
          });
          return (
            <UnifiedPostCell
              post={postLike}
              testId={`generation-draft-cell-${draft.id}`}
              metaChips={
                data?.format
                  ? <Chip size="small" variant="outlined" label={String(data.format)} />
                  : undefined
              }
              rightActions={
                <Tooltip title="Open details">
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (reviewRun) {
                        setEditingDraft({ runId: reviewRun.id, draft: data });
                      }
                    }}
                    data-testid={`generation-draft-edit-${draft.id}`}
                  >
                    <AutoFixHighIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
              onClick={() => {
                if (reviewRun) {
                  setEditingDraft({ runId: reviewRun.id, draft: data });
                }
              }}
            />
          );
        }}
      />

      {/* Unified post detail dialog — shared with Post examples + sprint posts.
          Wires the draft's Firestore write-back (useUpdateGenerationDraft) + the
          generate-image / upload-reference / save-as-example flows. */}
      {editingDraft && (
        <UnifiedPostDialog
          open={!!editingDraft}
          onClose={() => setEditingDraft(null)}
          post={contentPostToPostLike(editingDraft.draft, {
            kind: 'content_post_draft',
            projectId,
            runId: editingDraft.runId,
          })}
          onSave={async (next: PostLike): Promise<string> => {
            await updateDraft(editingDraft.draft.id, postLikeToContentPostPatch(next));
            return editingDraft.draft.id;
          }}
          onGenerateImage={async (config) => {
            try {
              const client = new ContentPlanApiClient();
              const resp = await client.generateImage({
                projectId, userId,
                ownerKind: 'content_post_draft',
                ownerId: editingDraft.draft.id,
                slotKey: config.slotKey,
                experimentId: config.experimentId ?? '',
                prompt: config.prompt,
                negativePrompt: config.negativePrompt,
                aspectRatio: config.aspectRatio,
                imageSize: config.imageSize,
                style: config.style,
                model: config.model,
                referenceImages: config.referenceImages,
              });
              if (!resp.success) return { success: false, error: resp.error };
              return { success: true };
            } catch (e) {
              return { success: false, error: (e as Error).message };
            }
          }}
          onUploadReference={async (file) => {
            const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
            const path = `projects/${projectId}/post_generations/${editingDraft.runId}/generatedPosts/${editingDraft.draft.id}/ref-images/${Date.now()}_${sanitizedName}`;
            const storage = new StorageService();
            const result = await storage.uploadImage(path, file);
            return { url: result.assetUrl.origin, mimeType: file.type || 'image/png' };
          }}
          // Promote-to-example flow temporarily disabled: legacy
          // post_examples collection removed (Task 11). Will be replaced
          // with author_posts.exampleForTopicIds tagging in Task 11 UI.
          onPromoteToExample={undefined}
        />
      )}
    </Stack>
  );
};

// ─── Main panel ──────────────────────────────────────────────────────

export const TopicDetailPanel: React.FC<TopicDetailPanelProps> = ({
  open, onClose, topic: inputTopic, projectId, userId, authorId, strategyId, sprintId,
  publicationId, publicationPlatform, publicationAspectKey, publicationSlotAssignment, onPostsGenerated,
  fullScreen = false, tab: tabProp, onTabChange,
}) => {
  const inputTopicIsStub = Boolean(inputTopic?.id) && Object.keys(inputTopic as object).length <= 1;
  const { topic: liveTopic, loading: liveTopicLoading } = useContentTopic(projectId, inputTopic?.id ?? null);
  const topic = liveTopic ?? inputTopic;

  // Controlled mode: caller provides BOTH `tab` and `onTabChange`. In that
  // case we mirror the prop into state but never run the "reset to overview"
  // effect (which is only meaningful for uncontrolled, in-app modal callers).
  const isControlled = tabProp !== undefined && typeof onTabChange === 'function';
  const [internalTab, setInternalTab] = useState<TabKey>('overview');
  const tab: TabKey = isControlled ? (tabProp as TabKey) : internalTab;
  const setTab = (next: TabKey) => {
    if (isControlled) {
      onTabChange!(next);
    } else {
      setInternalTab(next);
    }
  };

  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  // Sprint 17 — breadcrumb resolver for dataUsage paths (hook loads source
  // data once per project; safe to call even while loading).
  const { resolveLabel } = useResolveSourcePath(projectId);

  React.useEffect(() => {
    // Only reset in uncontrolled mode; the route page owns tab state when
    // `tab` + `onTabChange` are supplied.
    if (isControlled) return;
    if (open) setInternalTab('overview');
  }, [open, topic?.id, isControlled]);

  // ── Hooks must run unconditionally before the `if (!topic)` early return.
  // Phase B — resolve pillarIds → pillar.name for chips. Falls back to id
  // when pillar doc missing (e.g. legacy topic referencing deleted pillar).
  const { byId: pillarsById } = usePillars(projectId);
  const projectDefaultModel = useProjectDefaultModel(projectId);

  // Phase D — subscribe to COPE posts (master + derivatives) for this topic
  // so the "Generate post" CTA can react and the result lands inline.
  const { master: copeMaster, derivatives: copeDerivatives } =
    useMasterPostsForTopic(projectId, topic?.id ?? null);
  const { author } = useAuthor(projectId);
  const effectiveAuthorId = authorId ?? author?.id ?? null;
  const { platforms: authorPlatforms } = useAuthorPlatforms(projectId, effectiveAuthorId);
  const resolvedPostPlatforms = useMemo(() => {
    const topicPlatforms = ((topic as any)?.targetPlatforms ?? [])
      .map((platform: unknown) => String(platform || '').trim())
      .filter(Boolean);
    if (topicPlatforms.length > 0) return topicPlatforms;
    return authorPlatforms
      .filter((platform) => platform.isActive !== false)
      .map((platform) => String(platform.id || '').trim())
      .filter(Boolean);
  }, [topic, authorPlatforms]);
  const matchingPublicationIdForCell = (platform: string, aspectKey: string | null): string | undefined => {
    if (!publicationId) return undefined;
    if (publicationPlatform && publicationPlatform !== platform) return undefined;
    if ((publicationAspectKey ?? null) !== aspectKey) return undefined;
    return publicationId;
  };
  const [copeBusy, setCopeBusy] = useState(false);
  const [copeError, setCopeError] = useState<string | null>(null);
  const [copeModel, setCopeModel] = useState<string>(projectDefaultModel.value);

  React.useEffect(() => {
    if (open) setCopeModel(projectDefaultModel.value);
  }, [open, projectDefaultModel.value, topic?.id]);

  if (inputTopicIsStub && liveTopicLoading && !liveTopic) {
    return (
      <SDialog open={open} onClose={onClose} title="Topic" maxWidth="sm" fullWidth>
        <Stack direction="row" spacing={1.5} sx={{ py: 2, alignItems: 'center' }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading topic…</Typography>
        </Stack>
      </SDialog>
    );
  }

  if (!topic || (inputTopicIsStub && !liveTopicLoading && !liveTopic)) {
    return (
      <SDialog open={open} onClose={onClose} title="Topic" maxWidth="sm" fullWidth>
        <Box sx={{ py: 2 }}><em>{inputTopic?.id ? 'Topic not found' : 'No topic selected'}</em></Box>
      </SDialog>
    );
  }

  const themes = (topic.themeAssignment ?? []) as Array<{ themeId: string; weight?: number }>;
  const pillarIds = ((topic as any).pillarIds ?? []) as string[];
  const primaryPillarId = (topic as any).primaryPillarId as string | undefined;
  const keyMessage = (topic as any).keyMessage as string | undefined;
  const tags = ((topic as any).tags ?? []) as string[];
  const dataItems = (topic.dataUsage?.items ?? []) as Array<{ sourcePath?: string; justification?: string; value?: unknown }>;

  return (
    <>
      <SDialog
        open={open}
        onClose={onClose}
        title={topic.angle || 'Topic'}
        maxWidth="xl"
        fullWidth
        fullScreen={fullScreen}
        data-testid="topic-detail-panel"
        // Stable height — independent of how much each tab renders. Matches
        // SystemTemplateBrowserDialog height pattern (85vh) so tab switches
        // don't cause the dialog to jump. When rendered as a standalone
        // route page (`fullScreen`), fill the entire viewport instead.
        PaperProps={{
          sx: fullScreen
            ? { height: '100vh', maxHeight: '100vh' }
            : { height: '85vh', maxHeight: '85vh' },
        }}
      >
        <Box
          sx={{ minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
          data-testid={fullScreen ? 'topic-detail-panel-fullscreen' : undefined}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v as TabKey)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}
            data-testid="topic-detail-tabs"
          >
            <Tab label="Overview" value="overview" data-testid="topic-tab-overview" />
            <Tab label="Research" value="research" data-testid="topic-tab-research" />
            <Tab label="Templates" value="templates" data-testid="topic-tab-templates" />
            <Tab label="Voice & Design" value="voice" data-testid="topic-tab-voice" />
            <Tab label="Posts" value="posts" data-testid="topic-tab-posts" />
          </Tabs>

          <Box sx={{ pr: 1, pb: fullScreen ? 12 : 0 }}>
            {tab === 'overview' && (
              <Stack spacing={2} sx={{ py: 1 }}>
                {keyMessage && (
                  <Box data-testid="topic-detail-key-message">
                    <Typography variant="caption" color="text.secondary">KEY MESSAGE</Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{keyMessage}</Typography>
                  </Box>
                )}

                {/* workflow-golden-tower — surface the B2B reader contract that
                    already lives on the topic but was previously hidden. Renders
                    only when at least one B2B axis is set (B2C topics stay clean). */}
                <TopicB2BReaderContractCard topic={topic} />

                {/* Aspects editor — full inline table. Each row = one aspect = one
                    master post downstream (MasterPostHandler.aspectFocus resolves by
                    aspectKey). plannedPostCount is derived on save (= breakdown.length || 1)
                    so there's no separate count slider. Empty state explains the value
                    of aspects with concrete breakdown patterns. */}
                <Box data-testid="topic-detail-plan">
                  <PostBreakdownEditor
                    projectId={projectId}
                    topic={topic}
                    authorId={effectiveAuthorId}
                    userId={userId}
                  />
                </Box>

                {pillarIds.length > 0 && (
                  <Box data-testid="topic-detail-pillars">
                    <Typography variant="caption" color="text.secondary">PILLARS</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {pillarIds.map((pid) => {
                        const p = pillarsById[pid];
                        const isPrimary = pid === primaryPillarId;
                        const label = p?.name ?? pid;
                        return (
                          <Chip
                            key={pid}
                            label={isPrimary ? `${label} · primary` : label}
                            size="small"
                            color={isPrimary ? 'primary' : 'default'}
                            variant={isPrimary ? 'filled' : 'outlined'}
                            sx={{ mr: 0.5, mb: 0.5 }}
                            data-testid={`topic-detail-pillar-${pid}`}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {/* L.1 — Resources accumulator. Master post AI cites entries
                    when generating the platform-agnostic master, derivatives
                    inherit citations. */}
                <TopicResourcesSection
                  projectId={projectId}
                  topicId={topic.id}
                  inline
                />

                {/* Planned COPE path: route through /generate-posts so
                    PostGenerationPlanner runs before writers. */}
                <Box data-testid="topic-detail-cope">
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ mb: 0.5 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      POSTS (COPE)
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                      <AvailableModelSelector
                        projectId={projectId}
                        value={copeModel}
                        onChange={setCopeModel}
                        label="AI model"
                        size="small"
                        sx={{ minWidth: 240 }}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AutoFixHighIcon />}
                        data-testid="topic-detail-cope-generate-btn"
                        disabled={copeBusy || Boolean(publicationId)}
                        onClick={async () => {
                          setCopeError(null);
                          setCopeBusy(true);
                          try {
                            const client = new ContentPlanApiClient();
                            const targetPlatform = resolvedPostPlatforms[0];
                            if (!targetPlatform) throw new Error('Add an active platform before generating COPE posts.');
                            const response = await client.generatePosts({
                              projectId,
                              userId,
                              authorId: effectiveAuthorId ?? '',
                              scope: sprintId ? 'sprint' : 'topic',
                              strategyId,
                              sprintId,
                              publicationId: matchingPublicationIdForCell(targetPlatform, null),
                              topicIds: [topic.id],
                              platforms: [targetPlatform],
                              cellScope: {
                                aspectKey: null,
                                platform: targetPlatform,
                                cellMode: 'single',
                                method: 'cope',
                              },
                              applyMode: sprintId ? 'auto_apply_sprint' : 'auto_apply_topic',
                              models: { platformPost: copeModel },
                            } as any);
                            if (!response.success) throw new Error(response.error ?? 'post generation failed');
                          } catch (e: any) {
                            setCopeError(e?.message ?? 'Failed');
                          } finally {
                            setCopeBusy(false);
                          }
                        }}
                      >
                        {copeBusy
                          ? 'Generating…'
                          : copeMaster
                            ? 'Generate derivative'
                            : 'Generate master + derivative'}
                      </Button>
                    </Stack>
                  </Stack>
                  {publicationId && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Calendar slot mode uses the locked Posts cell generator.
                    </Typography>
                  )}
                  {copeError && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                      {copeError}
                    </Typography>
                  )}
                  {copeMaster && (
                    <Box
                      sx={{ p: 1.5, mb: 1, bgcolor: 'action.hover', borderRadius: 1 }}
                      data-testid="topic-detail-cope-master"
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Chip label="master" size="small" color="primary" />
                        <Typography variant="caption" color="text.secondary">
                          {copeMaster.id.slice(0, 12)}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {copeMaster.hook}
                      </Typography>
                      {copeMaster.body && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                        >
                          {copeMaster.body}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {copeDerivatives.length > 0 && (
                    <Stack spacing={1}>
                      {copeDerivatives.map((d) => (
                        <Box
                          key={d.id}
                          sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
                          data-testid={`topic-detail-cope-derivative-${d.id}`}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <Chip label={d.platform ?? 'unknown'} size="small" variant="outlined" />
                            <Typography variant="caption" color="text.secondary">
                              from {(d.sourcePostId ?? '').slice(0, 12)}
                            </Typography>
                            {typeof d.voiceMatchScore === 'number' && (
                              <Chip
                                label={`voice ${(d.voiceMatchScore * 100).toFixed(0)}%`}
                                size="small"
                              />
                            )}
                          </Stack>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {d.hook}
                          </Typography>
                          {d.body && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                            >
                              {d.body}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  )}
                  {!copeMaster && !copeBusy && copeDerivatives.length === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      No posts generated yet for this topic. Click Generate to author a master + a derivative for the first target platform.
                    </Typography>
                  )}
                </Box>

                {topic.summary && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">SUMMARY</Typography>
                    <Typography variant="body2">{topic.summary}</Typography>
                  </Box>
                )}

                <Box>
                  <Typography variant="caption" color="text.secondary">THEMES</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {themes.map((t) => (
                      <Chip
                        key={t.themeId}
                        label={`${t.themeId} (${((t.weight ?? 0) * 100).toFixed(0)}%)`}
                        size="small"
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ))}
                    {themes.length === 0 && <Typography variant="body2"><em>None</em></Typography>}
                  </Box>
                </Box>

                {tags.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">TAGS</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {tags.map((t) => (
                        <Chip key={t} label={t} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                    </Box>
                  </Box>
                )}

                <Box>
                  <Typography variant="caption" color="text.secondary">META</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Chip label={`status: ${topic.status}`} size="small" />
                    <Chip label={`weight: ${(topic as any).weight ?? 5}`} size="small" />
                    <Chip label={`usedIn: ${((topic as any).usedInSprintIds ?? []).length}`} size="small" />
                  </Stack>
                </Box>

                <DataUsageList
                  items={dataItems}
                  testIdPrefix="topic-data-usage"
                  resolveLabel={resolveLabel}
                />
              </Stack>
            )}

            {tab === 'research' && (
              <Box sx={{ py: 1 }}>
                <ResearchStudiesTab
                  projectId={projectId}
                  userId={userId}
                  authorId={effectiveAuthorId ?? ''}
                  topic={topic}
                />
              </Box>
            )}

            {tab === 'templates' && (
              <Box sx={{ py: 1 }}>
                <TopicTemplatesTab projectId={projectId} topic={topic} />
              </Box>
            )}

            {tab === 'voice' && (
              <Box sx={{ py: 1 }}>
                <VoiceDesignTab topic={topic} projectId={projectId} strategyId={strategyId} />
              </Box>
            )}

            {tab === 'posts' && (
              // Phase 2.5 — grid replaces the legacy PostsTabInline. Each cell
              // is one independent generation slot via PerCellGenerateDialog.
              // PostsTabInline is kept on disk in case the rollback is needed
              // mid-Phase 3 (multi-mode + sprint overlay), but no longer mounted.
              <Box sx={{ py: 1 }}>
                <PostsGrid
                  projectId={projectId}
                  userId={userId}
                  authorId={effectiveAuthorId ?? ''}
                  topic={topic}
                  strategyId={strategyId}
                  sprintId={sprintId}
                  publicationId={publicationId}
                  publicationPlatform={publicationPlatform}
                  publicationAspectKey={publicationAspectKey}
                  publicationSlotAssignment={publicationSlotAssignment}
                />
              </Box>
            )}
          </Box>
        </Box>
      </SDialog>

      <TopicPostGenerationDialog
        open={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
        projectId={projectId}
        userId={userId}
        authorId={effectiveAuthorId ?? ''}
        topic={topic}
        strategyId={strategyId}
        sprintId={sprintId}
        onSuccess={(r) => { onPostsGenerated?.(r.taskId); setGenerateDialogOpen(false); }}
      />
    </>
  );
};

export default TopicDetailPanel;
